import { apiPost } from '@/lib/http/client';
import { retryOn503 } from '@/lib/http/retry';
import type { FilterGroup, GroupByItem } from '@/types/crm';

export interface AggregationConfig {
  function: 'count' | 'sum' | 'avg' | 'min' | 'max';
  property?: string;
  alias: string;
}

export interface AggregateResultRow {
  group_by?: Record<string, unknown>;
  aggregations: Record<string, unknown>;
}

export interface AggregateResponse {
  count: number;
  results: AggregateResultRow[];
}

export interface AggregateRequest {
  entity_type_name: string;
  aggregations: AggregationConfig[];
  filter_groups?: FilterGroup[];
  group_by?: GroupByItem[];
  convert_to?: string;
}

export async function fetchAggregate(
  orgId: number,
  body: AggregateRequest
): Promise<AggregateResponse> {
  // dedupeInFlight: aggregate/ is read-only, and identical aggregate bodies
  // are issued concurrently by highlight widgets during WS-invalidation
  // refetch bursts — share one network call.
  return retryOn503(() =>
    apiPost<AggregateResponse>(
      `/api/orgs/${orgId}/crm-events/crm-objects/aggregate/`,
      body,
      undefined,
      { dedupeInFlight: true }
    )
  );
}

/**
 * Extract a numeric aggregation value. Handles both array and single-object inputs:
 * - QuantityValue[] (legacy array shape, grouped by unit) — sums the .amount fields
 * - {amount, unit} (single-object shape returned when convert_to is set)
 */
export function readAggregationNumber(
  rows: AggregateResultRow[],
  alias: string
): number {
  const raw = rows[0]?.aggregations?.[alias];
  if (typeof raw === 'number') {
    return raw;
  }
  if (Array.isArray(raw)) {
    // sum of a quantity property comes back as QuantityValue[] grouped by unit; sum the .amount fields
    return raw.reduce((acc, q) => {
      const amount = Number(
        (q as { amount?: string | number | null })?.amount ?? 0
      );
      return acc + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    // single-object shape returned when convert_to is set: {amount, unit}
    const amount = Number((raw as { amount?: number | string }).amount);
    return Number.isFinite(amount) ? amount : 0;
  }
  return 0;
}

/**
 * Reduce an aggregation result into a single {amount, unit} pair.
 * Accepts both array and single-object inputs:
 * - QuantityValue[] (legacy shape): amounts in different units cannot be added
 *   meaningfully (e.g. USD + EUR), so we sum within each unit independently
 *   and return the unit whose total amount is largest.
 * - {amount, unit} single-object shape returned when convert_to is set.
 *
 * Callers that need to render every unit should read `aggregations[alias]`
 * directly instead of using this helper.
 */
export function readAggregationQuantity(
  rows: AggregateResultRow[],
  alias: string
): { amount: number; unit: string | null } {
  const raw = rows[0]?.aggregations?.[alias];
  if (Array.isArray(raw) && raw.length > 0) {
    const totalsByUnit = new Map<string | null, number>();
    for (const entry of raw) {
      const q = entry as {
        amount?: string | number | null;
        unit?: string | null;
      };
      const unit = q?.unit ?? null;
      const parsed = Number(q?.amount ?? 0);
      const amount = Number.isFinite(parsed) ? parsed : 0;
      totalsByUnit.set(unit, (totalsByUnit.get(unit) ?? 0) + amount);
    }
    let dominantUnit: string | null = null;
    let dominantAmount = -Infinity;
    for (const [unit, amount] of totalsByUnit) {
      if (amount > dominantAmount) {
        dominantUnit = unit;
        dominantAmount = amount;
      }
    }
    // Every accumulated amount is finite (NaN coerced to 0 above), so if
    // totalsByUnit is non-empty dominantAmount is finite. Guard anyway.
    if (!Number.isFinite(dominantAmount)) {
      return { amount: 0, unit: null };
    }
    return { amount: dominantAmount, unit: dominantUnit };
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    // single-object shape returned when convert_to is set: {amount, unit}
    const q = raw as { amount?: string | number | null; unit?: string | null };
    const parsed = Number(q.amount ?? 0);
    const amount = Number.isFinite(parsed) ? parsed : 0;
    const unit = q.unit ?? null;
    return { amount, unit };
  }
  return { amount: typeof raw === 'number' ? raw : 0, unit: null };
}
